<template>
  <div class="col-12 h-100 common-card">
    <div class="chart-wrapper">
      <!-- 탭 UI -->
      <!-- <div class="tab-container">
     
      </div> -->

      <div class="common-card__title header-with-tabs">
        <h2 :style="{alignContent: 'center'}">{{ currentTitle }}</h2>

        <div class="tab-wrapper">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            :class="['tab-button', { active: activeTab === tab.key }]"
            @click="switchTab(tab.key)"
          >
            {{ tab.label }}
          </button>
        </div>
      </div>

      <!-- 금일 탭 -->
      <div v-if="activeTab === 'day'" class="day-tab">
        <!-- ① 날씨 레이어 (plot-area 위에 절대 위치) -->
        <div class="weather-row" ref="weatherRow">
          <div
            v-for="(w, i) in weatherData"
            :key="i"
            class="weather-cell"
          >
            <img class="weather-icon" v-if="w.icon && w.icon.includes('.png')" :src="w.icon" alt="맑음" width="40" height="40" />
            <div class="weather-icon" v-else :style="{display: 'flex', alignItems: 'center', justifyContent: 'center', placeSelf: 'center'}">
              <span>-</span>
            </div>
            <div class="temp">{{ w.temp !== null ? w.temp :'-' }}℃</div>
          </div>
        </div>

        <!-- ② ApexCharts 차트 -->
        <ClientOnly>
          <apexchart
            class="chart-area"
            id="geo-prod-usage-chart-day"
            chart-id="geo-prod-usage-chart-day"
            ref="chartRef"
            type="bar"
            :height="'85%'"
            :options="dayChartOptions"
            :series="daySeries"
          />
        </ClientOnly>
      </div>

      <!-- 금월/금년 탭 -->
      <div v-else class="month-year-tab">
        <ClientOnly>
          <apexchart
            class="chart-area chart-area--month"
            :id="`geo-prod-usage-chart-${activeTab}`"
            :chart-id="`geo-prod-usage-chart-${activeTab}`"
            type="bar"
            :height="'95%'"
            :options="monthYearChartOptions"
            :series="monthYearSeries"
          />
        </ClientOnly>
      </div>
      <div>
        <table class="table graph-bottom-table">
          <colgroup>
            <col width="25%">
            <col width="25%">
            <col width="25%">
            <col width="25%">
          </colgroup>
          <tbody>
            <tr>
              <td class="subject-td">에너지 생산량 합계 ({{ dateSetUnits }})</td>
              <td class="value-td text-center fw-bold">{{ commonUtils.isAllStrDataEmpty(periodTotalProd, 0) }}</td>
              <td class="subject-td">소비전력 합계 ({{ dateSetUnits }})</td>
              <td class="value-td text-center fw-bold">{{ commonUtils.isAllStrDataEmpty(periodTotalUsage, 0) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch, onMounted, onUpdated, onBeforeUnmount } from 'vue';
import moment from 'moment';
import { useCustomFetch } from '@/composables/useCustomFetch';
import CommonUtils from '@/utils/common-utils';
import { useEquipStore } from '~/store/equipStore';
import { useTocStore } from '~/store/tocStore';
import { useWidgetRangeStore } from '~/store/widgetRangeStore';
import { useSiteStore } from '~/store/siteStore';
import common from 'mocha/lib/interfaces/common';

const props = defineProps<{
  data?: any;
  property?: any;
  chartParams?: any[];
  intervalTime?: { time: number };
  pageType?: string;
}>();

const commonUtils = CommonUtils;
const equipStore = useEquipStore();
const siteStore = useSiteStore();
const tocStore = useTocStore();
const widgetRangeStore = useWidgetRangeStore();

// queryParams 값 가져오기 함수
function checkParamsValue(param: string) {
  switch(param) {
    case "now":         return moment().format('YYYY-MM-DD');
    case "startMonth": return moment().startOf('month').format('YYYY-MM-DD');
    case "endMonth":   return moment().endOf('month').format('YYYY-MM-DD');
    case "startYear":  return moment().startOf('year').format('YYYY-MM-DD');
    case "endYear":    return moment().endOf('year').format('YYYY-MM-DD');
    case "siteName":   return (tocStore.selectSiteInfo as any).siteName;
    case "cid":        return siteStore.selectedSite.cid;
    default:           return param;
  }
}

// 탭 관련
type TabKey = 'day' | 'month' | 'year';

const tabs: { key: TabKey; label: string }[] = [
  { key: 'day', label: '금일' },
  { key: 'month', label: '금월' },
  { key: 'year', label: '금년' }
];
const activeTab = ref<TabKey>('day');

const activeCid = computed(() => widgetRangeStore.selectCid);


// 차트 refs
const chartRef = ref(null);
const weatherRow = ref<HTMLElement | null>(null);

// 탭별 데이터
const tabData = ref<Partial<Record<TabKey, any>>>({});

// Interval 관리
const intervalTimers = ref<{ [key: string]: any }>({
  day: null,
  month: null,
  year: null
});
const intervalStartTimes = ref<{ [key: string]: number }>({});

// 금일 탭 데이터
const originWeatherData = ref<any[]>([]);
const weatherData = ref<any[]>([]);
const prodData = ref<(number | null)[]>([]);
const usageData = ref<(number | null)[]>([]);
const categories = ref<string[]>([]);

// 금월/금년 탭 데이터
const monthYearProdData = ref<(number | null)[]>([]);
const monthYearUsageData = ref<(number | null)[]>([]);
const monthYearCategories = ref<string[]>([]);

const periodTotalProd = ref<number | null>(null);
const periodTotalUsage = ref<number | null>(null);

// 현재 타이틀 (고정)
const currentTitle = computed(() => {
  return `지열시스템 생산량/소비전력 추이 (${dateSetUnits.value})`;
});

// const dateSetUnits = computed(() => {
//   let units = 'kWh';
//   if(activeTab.value === 'month' || activeTab.value === 'year') {
//     units = 'MWh';
//   } else if(activeTab.value === 'day') {
//     units = 'kWh';
//   }
//   return units;
// })



const dateSetUnits = ref('kWh');

// 카테고리 생성 함수 (ProdChart 참조)
function generateCategories(dateType: 'month' | 'year', length: number): string[] {
  const arr: string[] = [];
  if (dateType === 'year') {
    for (let i = 1; i <= length; i++) arr.push(`${i}월`);
  } else if (dateType === 'month') {
    for (let i = 1; i <= length; i++) arr.push(`${i}일`);
  }
  return arr;
}

// 탭 변경
function switchTab(tabKey: TabKey) {
  // 이전 탭의 interval 정리
  clearTabInterval(activeTab.value);

  periodTotalProd.value = null;
  periodTotalUsage.value = null;
  
  // 새 탭으로 변경
  activeTab.value = tabKey;

  // 전역 스토어에 선택 범위 업데이트
  widgetRangeStore.setRange(tabKey);
  
  // 새 탭의 API 호출
  fetchTabData(tabKey);
  
  // 새 탭의 interval 시작
  startTabInterval(tabKey);
}

// Interval 정리
function clearTabInterval(tabKey: TabKey) {
  if (intervalTimers.value[tabKey]) {
    clearTimeout(intervalTimers.value[tabKey]!);
    clearInterval(intervalTimers.value[tabKey]!);
    intervalTimers.value[tabKey] = null;
  }
}

// Interval 시작
function startTabInterval(tabKey: TabKey) {
  const intervalTime = props.intervalTime?.time || 900;
  
  // 남은 시간 계산
  let remainingTime = intervalTime;
  if (intervalStartTimes.value[tabKey]) {
    const elapsed = Date.now() - intervalStartTimes.value[tabKey];
    const elapsedSeconds = Math.floor(elapsed / 1000);
    remainingTime = Math.max(0, intervalTime - elapsedSeconds);
  }
  
  // 즉시 호출 (남은 시간이 거의 없거나 첫 호출인 경우)
  if (remainingTime < 60) {
    fetchTabData(tabKey);
    remainingTime = intervalTime;
  }
  
  // Interval 시작
  intervalTimers.value[tabKey] = setTimeout(() => {
    fetchTabData(tabKey);
    intervalStartTimes.value[tabKey] = Date.now();
    
    // 주기적 호출
    intervalTimers.value[tabKey] = setInterval(() => {
      fetchTabData(tabKey);
      intervalStartTimes.value[tabKey] = Date.now();
    }, intervalTime * 1000) as any;
  }, remainingTime * 1000) as any;
  
  intervalStartTimes.value[tabKey] = Date.now();
}

// API 호출
function getDummyPayload(tabKey: TabKey) {
  if (tabKey === 'day') {
    return createDummyDayData();
  }
  return {
    chartData: createDummyMonthYearData(tabKey)
  };
}

async function fetchTabData(tabKey: TabKey) {
  const apiConfig = props.data?.[tabKey];
  if (!apiConfig) return;
  
  try {
    // queryParams 처리 (BaseComponent 참조)
    const query: any = {};
    
    if (apiConfig.queryParams) {
      for (let i = 0; i < apiConfig.queryParams.length; i++) {
        const param = apiConfig.queryParams[i];
        if (param === 'engSource') {
          query[param] = equipStore.selectEquipInfo.engSource;
        } else if (param === 'siteName') {
          query[param] = (tocStore.selectSiteInfo as any).siteName;
        } else if (param === 'cid') {
          query[param] = equipStore.selectSiteInfo.cid;
        } else {
          // 기타 파라미터는 store에서 가져오기
          query[param] = (tocStore.selectSiteInfo as any)[param] || (equipStore.selectEquipInfo as any)[param];
        }
      }
    }
    
    // setting 처리
    if (apiConfig.setting) {
      const settingKeys = Object.keys(apiConfig.setting);
      for (let i = 0; i < settingKeys.length; i++) {
        query[settingKeys[i]] = checkParamsValue(apiConfig.setting[settingKeys[i]]);
      }
    }
    
    const { data, error } = await useCustomFetch(apiConfig.url, {
      method: apiConfig.method || 'GET',
      query: query,
    });
    
    let payload = data.value;

    if (!payload) {
      payload = getDummyPayload(tabKey as 'day' | 'month' | 'year');
    }

    periodTotalProd.value = payload ? payload.periodTotalProdKWH : null;
    periodTotalUsage.value = payload ? payload.periodTotalUsageKWH : null;


    (tabData.value as any)[tabKey] = payload;
    if (tabKey === 'day') {
      await processDayData(payload);
    } else if (tabKey === 'month' || tabKey === 'year') {
      await processMonthYearData(tabKey, payload);
    }

    if (error.value) {
      console.warn(`Fallback dummy data used for ${tabKey} due to API error.`);
    }
  } catch (error) {
    console.error(`Failed to fetch ${tabKey} data:`, error);
    const payload = getDummyPayload(tabKey as 'day' | 'month' | 'year');
    (tabData.value as any)[tabKey] = payload;
    if (tabKey === 'day') {
      await processDayData(payload);
    } else if (tabKey === 'month' || tabKey === 'year') {
      await processMonthYearData(tabKey, payload);
    }
  }
}

function createDummyDayData(apiData?: any = null) {
  const base = moment().startOf('day');
  const chartData = Array.from({ length: 24 }, (_, idx) => ({
    dateTime: base.clone().add(idx, 'hours').format('YYYY-MM-DD HH:mm:ss'),
    values: [
      Number((Math.random() * 40 + 20).toFixed(2)),
      Number((Math.random() * 25 + 10).toFixed(2))
    ]
  }));

  let weather = [];

  if(apiData && apiData.weather) {
    weather = apiData.weather;
  } else {
    weather = Array.from({ length: 8 }, (_, idx) => ({
      fcstDate: base.clone().add(idx * 3, 'hours').format('YYYYMMDDHH'),
      iconPath: null,
      temp: Math.round(Math.random() * 6 + 15),
      humidity: Math.round(Math.random() * 20 + 40)
    }));
  }

  // const weather = Array.from({ length: 8 }, (_, idx) => ({
  //   fcstDate: base.clone().add(idx * 3, 'hours').format('YYYYMMDDHH'),
  //   iconPath: null,
  //   temp: Math.round(Math.random() * 6 + 15),
  //   humidity: Math.round(Math.random() * 20 + 40)
  // }));

  return { chartData, weather };
}

function createDummyMonthYearData(type: 'month' | 'year') {
  const length = type === 'month' ? moment().daysInMonth() : 12;
  return Array.from({ length }, () => ({
    values: [
      Number((Math.random() * 60 + 30).toFixed(2)),
      Number((Math.random() * 40 + 15).toFixed(2))
    ]
  }));
}

// 금일 데이터 처리
async function processDayData(apiData: any) {
  originWeatherData.value = [];
  weatherData.value = [];
  categories.value = [];
  prodData.value = [];
  usageData.value = [];

  if (!apiData || !Array.isArray(apiData.chartData) || apiData.chartData.length === 0) {
    apiData = createDummyDayData(apiData);
  }

  if (apiData) {
    // 날씨 데이터 처리
    if (Array.isArray(apiData.weatherData)) {
      originWeatherData.value = apiData.weatherData.map((v: any) => {
        return {
          date: moment(v.fcstDate).format('YYYY-MM-DD HH'),
          icon: v.iconPath,
          temp: v.temp,
          hum: v.humidity
        };
      });
    }

    // 차트 데이터 처리
    if (Array.isArray(apiData.chartData)) {
      categories.value = apiData.chartData.map((v: any) => 
        `${moment(v.dateTime).format('HH')}시`
      );

      prodData.value = apiData.chartData.map((v: any) => 
        v.values && v.values[0] !== null ? Number(v.values[0].toFixed(2)) : null
      );

      usageData.value = apiData.chartData.map((v: any) => 
        v.values && v.values[1] !== null ? Number(v.values[1].toFixed(2)) : null
      );
    }

    // 날씨 데이터를 카테고리 길이에 맞춰 조정
    if (originWeatherData.value.length < categories.value.length) {
      for (let i = originWeatherData.value.length; i < categories.value.length; i++) {
        originWeatherData.value.push({
          date: moment(categories.value[i]).format('YYYY-MM-DD HH'),
          icon: null,
          temp: null,
          hum: null
        });
      }
    }

    // 3시간 간격으로 날씨 데이터 추출
    for (let i = 0; i < originWeatherData.value.length; i++) {
      if (i % 3 !== 0) continue;
      weatherData.value.push(originWeatherData.value[i]);
    }
  }
  
  // 데이터가 없거나 chartData가 없을 때도 카테고리 생성 (0시 ~ 23시)
  if (categories.value.length === 0) {
    for (let i = 0; i < 24; i++) {
      categories.value.push(`${i}시`);
      prodData.value.push(null);
      usageData.value.push(null);
    }
  }

  let result = prodData.value.some((v: number | null) => v !== null && v > 7000) ||
              usageData.value.some((v: number | null) => v !== null && v > 7000);

  if(result){
    dateSetUnits.value = 'MWh';
    prodData.value = prodData.value.map((v: number | null) => v !== null ? v / 1000 : null);
    usageData.value = usageData.value.map((v: number | null) => v !== null ? v / 1000 : null);
  } else {
    dateSetUnits.value = 'kWh';
  }

  
  await nextTick();
  alignWeather();
}

// 금월/금년 데이터 처리 (ProdChart 참조)
async function processMonthYearData(tabKey: 'month' | 'year', apiData: any) {
  monthYearProdData.value = [];
  monthYearUsageData.value = [];
  monthYearCategories.value = [];

  const dateType = tabKey;
  const dateLength = dateType === 'month' ? moment().daysInMonth() : 12;
  
  // 기본 카테고리 생성 (데이터가 없어도 표시)
  monthYearCategories.value = generateCategories(dateType, dateLength);

  let chartData;
  if (apiData) {
    chartData = apiData.chartData ?? apiData;
  }

  if (!Array.isArray(chartData) || chartData.length === 0) {
    chartData = createDummyMonthYearData(tabKey);
  }

  monthYearProdData.value = chartData.map((v: any) => 
    v.values && v.values[0] !== null ? Number(v.values[0].toFixed(2)) : null
  );
  
  monthYearUsageData.value = chartData.map((v: any) => 
    v.values && v.values[1] !== null ? Number(v.values[1].toFixed(2)) : null
  );
  
  // 데이터 길이가 카테고리 길이와 다를 경우 조정
  if (monthYearProdData.value.length < monthYearCategories.value.length) {
    const diff = monthYearCategories.value.length - monthYearProdData.value.length;
    for (let i = 0; i < diff; i++) {
      monthYearProdData.value.push(null);
      monthYearUsageData.value.push(null);
    }
  }


  let result = monthYearProdData.value.some((v: number | null) => v !== null && v > 7000) ||
              monthYearUsageData.value.some((v: number | null) => v !== null && v > 7000);

  if(result){
    dateSetUnits.value = 'MWh';
    monthYearProdData.value = monthYearProdData.value.map((v: number | null) => v !== null ? v / 1000 : null);
    monthYearUsageData.value = monthYearUsageData.value.map((v: number | null) => v !== null ? v / 1000 : null);
  } else {
    dateSetUnits.value = 'kWh';
  }


}

// Y축 최대값 계산 (금일)
const y1maxProd = computed(() => {
  const filtered = prodData.value.filter(v => v !== null && v !== undefined);
  return filtered.length > 0 ? Math.max(...filtered) : 0;
});

const y1maxUsage = computed(() => {
  const filtered = usageData.value.filter(v => v !== null && v !== undefined);
  return filtered.length > 0 ? Math.max(...filtered) : 0;
});

const y1bufferedMax = computed(() => {
  const max = Math.max(y1maxProd.value, y1maxUsage.value);
  return max > 0 ? Math.ceil(max * 1.1) : 100;
});

// Y축 최대값 계산 (금월/금년)
const monthYearY1maxProd = computed(() => {
  const filtered = monthYearProdData.value.filter(v => v !== null && v !== undefined);
  return filtered.length > 0 ? Math.max(...filtered) : 0;
});

const monthYearY1maxUsage = computed(() => {
  const filtered = monthYearUsageData.value.filter(v => v !== null && v !== undefined);
  return filtered.length > 0 ? Math.max(...filtered) : 0;
});

const monthYearY1bufferedMax = computed(() => {
  const max = Math.max(monthYearY1maxProd.value, monthYearY1maxUsage.value);
  return max > 0 ? Math.ceil(max * 1.1) : 100;
});

// 금일 차트 옵션
const dayChartOptions = computed(() => {
  return {
    chart: {
      id: 'geo-prod-usage-chart-day',
      type: 'bar',
      toolbar: { show: false },
      stacked: false,
      events: {
        mounted: (ctx: any) => { alignWeather(); },
        updated: (ctx: any) => { alignWeather(); },
        animationEnd: (ctx: any) => { alignWeather(); }
      }
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '60%',
        borderRadius: 4,
        dataLabels: { position: 'top' }
      }
    },
    legend: {
      show: false,
      position: 'top',
      horizontalAlign: 'right',
      fontSize: '12px',
      markers: {
        width: 12,
        height: 12,
        radius: 6
      }
    },
    tooltip: {
      shared: true,
      intersect: false,
      valueFormatter: (value: number) => {

        if (value === null || value === undefined) return '-';
        return commonUtils.numberWithCommas(value, 2) + ` ${dateSetUnits.value}`;
      }
    },
    dataLabels: {
      enabled: false
    },
    grid: {
      padding: { top: 20, right: 0, bottom: 0, left: 0 },
      borderColor: '#a3a3a3',
      strokeDashArray: 4,
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: false } }
    },
    xaxis: {
      categories: categories.value,
      tickPlacement: 'between',
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        rotateAlways: false,
        rotate: 0,
        style: {
          fontSize: "8px",
          fontWeight: 700
        }
      }
    },
    yaxis: {
      decimalsInFloat: 0,
      labels: {
        style: { colors: '#0687f1' },
        offsetX: -10,
        formatter: (value: number) => {
          if (value === 0) return value;
          if (value === null || value === undefined) return '-';
          return commonUtils.isAllStrDataEmpty(value, 1);
        }
      },
      max: y1bufferedMax.value,
      min: 0
    },
    stroke: {
      curve: 'smooth',
      width: [0, 3]
    },
    markers: {
      size: [0, 4],
      hover: {
        size: 6
      }
    },
    colors: ['#A0E58B', '#FFBC93'],
  };
});

// 금일 시리즈
// 시리즈 길이 보정 (금일)
function ensureDaySeriesLength(targetLength?: number) {
  const expectedLength = targetLength ?? categories.value.length ?? 24;
  while (prodData.value.length < expectedLength) {
    prodData.value.push(null);
  }
  while (usageData.value.length < expectedLength) {
    usageData.value.push(null);
  }
}

const daySeries = computed(() => {
  ensureDaySeriesLength(categories.value.length || 24);
  return [
    {
      name: `생산량(${dateSetUnits.value})`,
      type: 'column',
      data: [...prodData.value]
    },
    {
      name: `소비전력(${dateSetUnits.value})`,
      type: 'line',
      data: [...usageData.value]
    }
  ];
});

// 금월/금년 차트 옵션 (ProdChart 참조 + 막대+선 그래프)
const monthYearChartOptions = computed(() => {
  return {
    chart: {
      id: 'geo-prod-usage-chart-monthyear',
      type: 'bar',
      toolbar: { show: false },
      stacked: false
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '60%',
        borderRadius: 4,
        dataLabels: { position: 'top' }
      }
    },
    legend: {
      show: false
    },
    tooltip: {
      shared: true,
      intersect: false,
      valueFormatter: (value: number) => {
        if (value === null || value === undefined) return '-';
        return commonUtils.numberWithCommas(value, 2) + ` ${dateSetUnits.value}`;
      }
    },
    dataLabels: {
      enabled: false
    },
    grid: {
      padding: { top: 20, right: 0, bottom: 0, left: 0 },
      borderColor: '#a3a3a3',
      strokeDashArray: 4,
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: false } }
    },
    xaxis: {
      categories: monthYearCategories.value,
      tickPlacement: 'between',
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        rotateAlways: false,
        rotate: 0,
        style: {
          fontSize: "8px",
          fontWeight: 700
        }
      }
    },
    yaxis: {
      decimalsInFloat: 0,
      labels: {
        style: { colors: '#0687f1' },
        offsetX: -10,
        formatter: (value: number) => {
          if (value === 0) return value;
          if (value === null || value === undefined) return '-';
          return commonUtils.isAllStrDataEmpty(value, 1);
        }
      },
      max: monthYearY1bufferedMax.value,
      min: 0
    },
    stroke: {
      curve: 'smooth',
      width: [0, 3]
    },
    markers: {
      size: [0, 4],
      hover: {
        size: 6
      }
    },
    colors: ['#A0E58B', '#FFBC93']
  };
});

// 금월/금년 시리즈 (막대+선 그래프)
// 시리즈 길이 보정 (금월/금년)
function ensureMonthYearSeriesLength(targetLength?: number) {
  const expectedLength = targetLength ?? monthYearCategories.value.length ?? 0;
  while (monthYearProdData.value.length < expectedLength) {
    monthYearProdData.value.push(null);
  }
  while (monthYearUsageData.value.length < expectedLength) {
    monthYearUsageData.value.push(null);
  }
}

const monthYearSeries = computed(() => {
  ensureMonthYearSeriesLength(monthYearCategories.value.length);
  return [
    {
      name: `생산량(${dateSetUnits.value})`,
      type: 'column',
      data: [...monthYearProdData.value]
    },
    {
      name: `소비전력(${dateSetUnits.value})`,
      type: 'line',
      data: [...monthYearUsageData.value]
    }
  ];
});

// 날씨 셀 정렬 함수
function alignWeather() {
  const comp = chartRef.value;
  const row = weatherRow.value;
  if (!comp || !row) return;

  const apex = (comp as any).chart;
  if (!apex || !apex.w || !apex.w.globals) return;
  
  const { translateX, gridWidth } = apex.w.globals;
  const N = categories.value.length;
  const step = gridWidth * 3 / N;
  const base = translateX + step / 2;

  row.style.left = '0';
  row.style.width = '100%';

  Array.from(row.children).forEach((child, i) => {
    const cell = child as HTMLElement;
    cell.style.position = 'absolute';
    cell.style.top = '0';
    cell.style.width = `${gridWidth * 3 / N}px`;
    cell.style.left = `${base + step * i}px`;
    cell.style.transform = 'translateX(-50%)';
  });
}

// X축 라벨 동기화
watch(categories, (newValue) => {
  if (chartRef.value && newValue) {
    (chartRef.value as any).updateOptions?.({
      xaxis: {
        categories: [...newValue]
      }
    }, false, true, true);
  }
});

watch(activeCid, (newValue) => {
  if(newValue) {
    switchTab(activeTab.value);
  }
});


// 컴포넌트 마운트 시
onMounted(() => {
  widgetRangeStore.setRange(activeTab.value);
  nextTick(async () => {
    // 초기 탭 데이터 로드
    await fetchTabData(activeTab.value);
    startTabInterval(activeTab.value);
  });
});

// 컴포넌트 언마운트 시
onBeforeUnmount(() => {
  (['day', 'month', 'year'] as TabKey[]).forEach(tabKey => {
    clearTabInterval(tabKey);
  });
});
</script>

<style lang="scss" scoped>
@use "@/assets/styles/design-toc/define_font.scss";
@use "@/assets/styles/design-toc/common-card.scss";
@import "@/assets/styles/design-toc/weather-prod-chart.scss";

.common-card {
  padding: 0.5rem 1rem !important;

  &__title {
    margin-bottom: 10px;
    h2 {
      line-height: 150%;
      @include define_font.text-bold-16;
    }
  }
}

.graph-bottom-table{
  border-collapse: separate;
  border-spacing: 0;
  vertical-align: middle;
  tbody{
    tr{
      td{
        padding:0.25rem 0.4rem;
        border: 1px solid #e5e5e5;
        text-align: center;
        &.subject-td{
          background-color: rgb(202, 238, 251);
          color: #464A53;
          font-weight: bold;
        }

        &.value-td{
          background-color: #f7f7f7
        }

        &:nth-child(1){
          border-top-left-radius: 6px;
          border-bottom-left-radius: 6px;
        }
        &:nth-last-child(1){
          border-top-right-radius: 6px;
          border-bottom-right-radius: 6px;
        }
      }
    }
  }
}

.header-with-tabs {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.tab-wrapper {
  display: inline-flex;
  border: 1px solid #d5deed;
  border-radius: 999px;
  overflow: hidden;
  background: #f3f6fb;
}

.tab-button {
  padding: 0.4rem 1.2rem;
  border: none;
  background: transparent;
  font-size: 13px;
  font-weight: 500;
  color: #5c6b85;
  transition: all 0.2s ease;
  min-width: 64px;

  & + .tab-button {
    border-left: 1px solid #d5deed;
  }

  &.active {
    background: #007ce1;
    color: #fff;
    font-weight: 600;
  }

  &:hover:not(.active) {
    background: rgba(0, 124, 225, 0.12);
    color: #007ce1;
  }
}

.chart-wrapper {
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  gap: 0.25rem;
}

.day-tab,
.month-year-tab {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  margin-top: 0.25rem;
}

.day-tab apexchart,
.month-year-tab apexchart {
  flex: 1;
  min-height: 0;
}

.day-tab {
  position: relative;

  .chart-area {
    margin-top: 1.55rem;
    height: calc(100% - 0.75rem);
  }
}

.chart-area {
  flex: 1;
  min-height: 0;
  width: 100%;
  display: flex;
}

.chart-area--month {
  margin-top: -1rem;
  height: 100%;
  padding-bottom: 0;
  padding-top: 0.25rem;
}

:deep(.chart-area .apexcharts-canvas),
:deep(.chart-area .apexcharts-canvas svg) {
  height: 100% !important;
}

:deep(.chart-area .apexcharts-outer) {
  height: 100% !important;
}

.weather-cell {
  padding-bottom: 10px;
}

.weather-row {
  top: 10px !important;
}

.temp {
  line-height: 1.2 !important;
  margin-top: 2px !important;
  margin-bottom: 0 !important;
}
</style>
